import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { ScrollFade, useScrollEdgeFades } from '../components/ScrollFade'
import { SearchInput } from '../components/SearchInput'
import { SelectableAssetRow } from '../components/SelectableAssetRow'
import { TopBar } from '../components/TopBar'
import { useNestedSheetHeight } from '../hooks/useNestedSheetHeight'
import { TokenChainIcon } from '../icons/TokenChainIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, useTheme } from '../theme/restyle'

const ROW_AVATAR_SIZE = 36

export interface SelectReceiveTokenScreenProps {
  tokens: string[]
  selectedToken?: string
  /** Drafts the token and hands control back to the screen underneath. */
  onSelectToken: (token: string) => void
  onClose: () => void
}

/**
 * Every token a withdrawal can be received as, searchable — the tail behind the
 * grid's `+N tokens` row. A tap drafts and returns; only the grid commits.
 */
export function SelectReceiveTokenScreen({
  tokens,
  selectedToken,
  onSelectToken,
  onClose,
}: SelectReceiveTokenScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const sheetHeight = useNestedSheetHeight()
  const insets = useSafeAreaInsets()
  const { showTopFade, showBottomFade, scrollProps } = useScrollEdgeFades()
  const [query, setQuery] = useState('')

  const needle = query.trim().toLowerCase()
  const orderedTokens =
    selectedToken && tokens.includes(selectedToken)
      ? [selectedToken, ...tokens.filter((token) => token !== selectedToken)]
      : tokens
  const matches = orderedTokens.filter((token) =>
    token.toLowerCase().includes(needle),
  )
  const searchPlaceholder = t('selectReceiveToken.searchTokens')

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={{
        height: sheetHeight,
        backgroundColor: theme.background.modal,
      }}
    >
      <TopBar
        title={t('selectReceiveToken.availableTokens')}
        onClose={onClose}
      />

      <Box style={styles.search}>
        <SearchInput
          value={query}
          onChangeValue={setQuery}
          placeholder={searchPlaceholder}
          accessibilityLabel={searchPlaceholder}
        />
      </Box>

      <View style={styles.scrollArea}>
        <BottomSheetScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.list,
            { paddingBottom: insets.bottom + LIST_BOTTOM_GAP },
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          {...scrollProps}
        >
          {matches.map((token) => (
            <SelectableAssetRow
              key={token}
              name={token}
              icon={<TokenChainIcon symbol={token} size={ROW_AVATAR_SIZE} />}
              selected={token === selectedToken}
              onPress={() => onSelectToken(token)}
              accessibilityLabel={token}
            />
          ))}
        </BottomSheetScrollView>

        {showTopFade ? (
          <ScrollFade
            edge="top"
            color={theme.background.modal}
            id="receive-token-list"
          />
        ) : null}
        {showBottomFade ? (
          <ScrollFade
            edge="bottom"
            color={theme.background.modal}
            id="receive-token-list"
          />
        ) : null}
      </View>
    </Box>
  )
}

// Read off the inset so the last row clears the home indicator on any device.
const LIST_BOTTOM_GAP = 16

const styles = StyleSheet.create({
  search: { paddingHorizontal: 24, paddingBottom: 12 },
  scrollArea: { position: 'relative', flex: 1 },
  scroll: { flex: 1 },
  list: { paddingHorizontal: 16 },
})
